import React from 'react';
import { Box, Checkbox, IconButton, TableCell, TableHead as MUITableHead, TableRow as MUITableRow, TableSortLabel, SvgIconProps, SvgIcon, Tooltip } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { FieldHeaders, TableFields } from './fieldsConfigTypes';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';

export type Order = 'asc' | 'desc';

interface HeadCell<T> {
  disablePadding: boolean;
  field: keyof T;
  headerName: string;
  numeric: boolean;
}

function generateHeadCells<T>(tableFields: TableFields<never>, fieldHeaders: FieldHeaders): HeadCell<T>[] {
  return tableFields.map((field, index) => {
    const headerName = field ? (fieldHeaders[field as string] || String(field)) : '';
    return {
      field,
      headerName,
      disablePadding: index === 0, // Only first column has disablePadding
      numeric: false
    } as HeadCell<T>;
  });
}

interface TableHeadProps<T> {
  numSelected?: number;
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof T, ctrlKey?: boolean) => void;
  onSelectAllClick?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onExpandAll?: () => void;
  onCollapseAll?: () => void;
  order: Order;
  orderBy: string;
  rowCount: number;
  tableFields: TableFields<never>;
  fieldHeaders: FieldHeaders;
}

function TableHead<T>(props: TableHeadProps<T>) {
  const { onSelectAllClick, order, orderBy, numSelected = 0, rowCount, onRequestSort } = props;

  const headCells = React.useMemo(() => generateHeadCells(props.tableFields, props.fieldHeaders), [props.tableFields, props.fieldHeaders]);

  const createSortHandler = (property: keyof T) => (event: React.MouseEvent<unknown>) => {
    if (property) {
      onRequestSort(event, property, event.ctrlKey);
    }
  };

  return (
    <MUITableHead>
      <MUITableRow
        sx={{
          height: '44px', // Reduce height to match data rows
          '& > th': {
            padding: '6px 16px', // Reduced vertical padding to match data cells
          }
        }}
      >
        {headCells.map((headCell, index) => (
          <TableCell
            key={headCell.field || `column-${index}`}
            align="left"
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.field ? order : false}
          >
            {index === 0 && onSelectAllClick ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Checkbox
                  indeterminate={numSelected > 0 && numSelected < rowCount}
                  checked={rowCount > 0 && numSelected === rowCount}
                  onChange={onSelectAllClick}
                  inputProps={{ 'aria-label': 'select all row' }}
                />
                {props.onExpandAll && props.onCollapseAll && (
                  <>
                    <Tooltip title="Expand all">
                      <IconButton
                        size="small"
                        onClick={props.onExpandAll}
                        sx={{ p: 0.25, color: 'text.secondary' }}
                      >
                        <UnfoldMoreIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Collapse all">
                      <IconButton
                        size="small"
                        onClick={props.onCollapseAll}
                        sx={{ p: 0.25, color: 'text.secondary' }}
                      >
                        <UnfoldLessIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Box>
            ) : (
              <TableSortLabel
                active={orderBy === headCell.field}
                direction={orderBy === headCell.field ? order : 'asc'}
                onClick={createSortHandler(headCell.field)}
                IconComponent={FaBoltIcon}
              >
                {headCell.headerName}
                {orderBy === headCell.field ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            )}
          </TableCell>
        ))}
      </MUITableRow>
    </MUITableHead>
  );
}

function FaBoltIcon(props: SvgIconProps) {
  return (
    <SvgIcon {...props} viewBox='0 0 576 512' xmlns="http://www.w3.org/2000/svg">
      <path d="M151.6 469.6C145.5 476.2 137 480 128 480s-17.5-3.8-23.6-10.4l-88-96c-11.9-13-11.1-33.3 2-45.2s33.3-11.1 45.2 2L96 365.7 96 64c0-17.7 14.3-32 32-32s32 14.3 32 32l0 301.7 32.4-35.4c11.9-13 32.2-13.9 45.2-2s13.9 32.2 2 45.2l-88 96zM320 480c-17.7 0-32-14.3-32-32s14.3-32 32-32l32 0c17.7 0 32 14.3 32 32s-14.3 32-32 32l-32 0zm0-128c-17.7 0-32-14.3-32-32s14.3-32 32-32l96 0c17.7 0 32 14.3 32 32s-14.3 32-32 32l-96 0zm0-128c-17.7 0-32-14.3-32-32s14.3-32 32-32l160 0c17.7 0 32 14.3 32 32s-14.3 32-32 32l-160 0zm0-128c-17.7 0-32-14.3-32-32s14.3-32 32-32l224 0c17.7 0 32 14.3 32 32s-14.3 32-32 32L320 96z" />
    </SvgIcon>
  );
}
export default TableHead;